import { useEffect, useState } from "react";
import { Alert, App, Button, Descriptions, Empty, Form, Input, Modal, Result, Select, Space, Statistic, Steps, Table, Tooltip } from "antd";
import {
  approveAdjustment,
  closeStockCount,
  createAdjustment,
  listAdjustments,
  openStockCount,
  rejectAdjustment,
  scanStockCount,
  type AdjustmentRow,
  type StockClose,
  type StockOpen,
  type StockScan,
} from "../api/client";
import { AdminScanNotice, formatWhen, PasswordModal, requestStatusLabel, ScanBox } from "../components/widgets";
import { canManage, useSession } from "../session";

export function Stocktake(): JSX.Element {
  const { message } = App.useApp();
  const { user } = useSession();
  const manager = canManage(user);
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState<StockOpen | null>(null);
  const [scans, setScans] = useState<StockScan[]>([]);
  const [diff, setDiff] = useState<StockClose | null>(null);
  const [adjustments, setAdjustments] = useState<AdjustmentRow[]>([]);
  const [noteOpen, setNoteOpen] = useState(false);
  const [passwordId, setPasswordId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [form] = Form.useForm<{ kind: "SHORTAGE" | "DAMAGE" | "SURPLUS"; note: string }>();

  function reloadAdjustments(): void {
    void listAdjustments().then(setAdjustments).catch(() => undefined);
  }

  useEffect(() => {
    reloadAdjustments();
  }, []);

  return (
    <>
      <AdminScanNotice />
      <Steps current={step} items={[{ title: "扫库位" }, { title: "逐双扫实物" }, { title: "看差异并提交调整" }]} />
      {step === 0 ? (
        <>
          <Empty description="还没开始盘点。先扫一个库位码。" />
          <ScanBox
            placeholder="扫描要盘的库位码"
            onSubmit={async (code) => {
              try {
                const created = await openStockCount(code);
                setOpen(created);
                setStep(1);
                setScans([]);
                setDiff(null);
              } catch (error: unknown) {
                message.error(error instanceof Error ? error.message : "没有这个库位。请扫库位上的库位码。");
              }
            }}
          />
        </>
      ) : null}
      {open && step >= 1 ? (
        <Descriptions
          style={{ marginTop: 16 }}
          items={[
            { key: "loc", label: "库位", children: open.locationCode },
            { key: "batch", label: "系统里的批次", children: open.batchNo ?? "空着" },
            { key: "qty", label: "系统双数", children: open.expected.length },
          ]}
        />
      ) : null}
      {step === 1 && open ? (
        <>
          {open.expected.length === 0 ? <Alert type="info" showIcon message="这个库位账上是空的。若手里有鞋，逐双扫上来，再提交盘盈。" /> : null}
          <ScanBox
            placeholder="扫描这个库位里的每一双"
            onSubmit={async (code) => {
              const row = await scanStockCount(open.id, code);
              if (row.duplicate) message.warning(row.message ?? "这双刚才已经盘过，没有重复计数。");
              else if (row.kind === "FOREIGN") message.error(row.note ?? "这双不在你正在盘的位置。");
              setScans((current) => [...current, row]);
            }}
          />
          <Table
            rowKey={(row) => `${row.serial}-${scans.indexOf(row)}`}
            dataSource={scans}
            columns={[
              { title: "序列号", dataIndex: "serial" },
              { title: "SKU", dataIndex: "humanCode" },
              { title: "批次", dataIndex: "batchNo" },
              { title: "是否属于本库位", render: (_v: unknown, row: StockScan) => row.kind === "FOREIGN" ? "不在此库位" : "扫到" },
            ]}
          />
          <Button
            type="primary"
            onClick={() => {
              void closeStockCount(open.id).then((result) => {
                setDiff(result);
                setStep(2);
              });
            }}
          >
            生成本次差异清单
          </Button>
        </>
      ) : null}
      {diff ? (
        <>
          <Space style={{ margin: "16px 0" }}>
            <Statistic title="系统双数" value={diff.expected} />
            <Statistic title="实盘双数" value={diff.scanned} />
            <Statistic title="相差" value={diff.scanned - diff.expected} />
          </Space>
          {diff.missing.length === 0 && diff.foreign.length === 0 && diff.unknown.length === 0 ? (
            <Result status="success" title="这个库位账实一致" subTitle="不用提交调整。" />
          ) : (
            <Button onClick={() => setNoteOpen(true)}>把差异提交给主管</Button>
          )}
          <Table
            rowKey="serial"
            dataSource={[
              ...diff.matched.map((serial) => ({ serial, result: "扫到" })),
              ...diff.missing.map((serial) => ({ serial, result: "系统有账但没扫到" })),
              ...diff.foreign.map((serial) => ({ serial, result: "扫到但不在此库位" })),
              ...diff.unknown.map((serial) => ({ serial, result: "这个号不在档案里，请走脱落补挂，不要在盘点里新建号。" })),
            ]}
            columns={[
              { title: "序列号", dataIndex: "serial" },
              { title: "实盘结果", dataIndex: "result" },
            ]}
          />
        </>
      ) : null}
      <Table
        style={{ marginTop: 24 }}
        title={() => "待审批调整"}
        rowKey="adjustNo"
        dataSource={adjustments}
        locale={{ emptyText: "还没有调整单。" }}
        columns={[
          { title: "时间", dataIndex: "createdAt", render: (value: string) => formatWhen(value) },
          { title: "单号", dataIndex: "adjustNo" },
          { title: "类型", dataIndex: "kind", render: (value: string) => kindLabel(value) },
          { title: "库位", dataIndex: "locationCode" },
          { title: "相差", render: (_v: unknown, row: AdjustmentRow) => row.serials.length },
          { title: "申请人", dataIndex: "applicantName" },
          { title: "说明", dataIndex: "note" },
          { title: "状态", dataIndex: "status", render: (value: string) => requestStatusLabel(value) },
          {
            title: "操作",
            render: (_v: unknown, row: AdjustmentRow) => manager && row.status === "PENDING" ? (
              <Space>
                <Tooltip title={row.applicantId === user?.id ? "这张申请是你提交的，要换主管账号来通过。" : ""}>
                  <Button disabled={row.applicantId === user?.id} onClick={() => setPasswordId(row.id)}>批准调整并改库存</Button>
                </Tooltip>
                <Button onClick={() => setRejectId(row.id)}>退回这张调整单</Button>
              </Space>
            ) : "—",
          },
        ]}
      />
      <Modal
        destroyOnClose
        open={noteOpen}
        title="提交调整单"
        okText="提交调整单给主管"
        cancelText="先不操作"
        onCancel={() => setNoteOpen(false)}
        onOk={() => form.submit()}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            if (!open || !diff) return;
            const serials = [...diff.missing, ...diff.foreign].filter((serial) => /^\d{8}$/.test(serial));
            void createAdjustment({
              locationCode: open.locationCode,
              kind: values.kind,
              note: values.note,
              serials,
            }).then(() => {
              message.success("差异已交给主管。在通过之前，库存数字先不变。");
              setNoteOpen(false);
              reloadAdjustments();
            });
          }}
        >
          <Form.Item label="调整类型" name="kind" rules={[{ required: true, message: "请选择调整类型。" }]}>
            <Select options={[{ value: "SHORTAGE", label: "盘亏" }, { value: "DAMAGE", label: "报损" }, { value: "SURPLUS", label: "盘盈" }]} />
          </Form.Item>
          <Form.Item label="说明" name="note" rules={[{ required: true, message: "请写清这次差在哪。" }]}>
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        destroyOnClose
        open={rejectId !== null}
        title="退回这张调整单"
        okText="把调整单退回"
        cancelText="先不操作"
        onCancel={() => setRejectId(null)}
        onOk={() => {
          if (!rejectId) return Promise.reject(new Error("empty"));
          return rejectAdjustment(rejectId, rejectReason || "请改完再交").then(() => {
            message.success("已退回给申请人。对方可以改原因后再交。");
            setRejectId(null);
            reloadAdjustments();
          });
        }}
      >
        <Input.TextArea value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} />
      </Modal>
      <PasswordModal
        open={passwordId !== null}
        okText="确认本人并按这张单改库存"
        onCancel={() => setPasswordId(null)}
        onConfirm={async (secret) => {
          if (!passwordId) return;
          await approveAdjustment(passwordId, secret);
          message.success("已按调整单改完库存。可以在时光轴里查到是谁批的。");
          setPasswordId(null);
          reloadAdjustments();
        }}
      />
    </>
  );
}

function kindLabel(value: string): string {
  if (value === "SHORTAGE") return "盘亏";
  if (value === "DAMAGE") return "报损";
  if (value === "SURPLUS") return "盘盈";
  return value;
}
